import moment from 'moment';
import { filter, keyBy, sortBy, keys, isNil } from 'lodash';

import * as types from 'ui-permit/src/constants';
import { feeTypes, convertLineItemsToFeesMap, readRuleValue, issuanceStatuses } from 'ui-permit/src/utils/utils';
import { MODIFICATION_DEADLINE } from 'ui-permit/shared-components/constants/rules';

import { LOTTERY_WON, PERMIT_IN_PROGRESS, PERMIT_PRELIMINARY } from '../constants';

export const INYO_NF_PERMIT_ID = '233262';
export const MT_WHITNEY_PERMIT_ID = '233260';
export const MT_WHITNEY_DAY_USE_DIVISION_ID = '406';

export const INYO_MT_WHITNEY_EXIT_ID = '516';
export const INYO_NF_MAP_COORDINATES = [-118.4027778, 37.3527778];
export const MTWHITNEY_VISIT_TRAILS = ['512'];
export const INYO_COMMERCIAL_TRAILS = ['4951', '4952'];

// Permit Types
export const OVERNIGHT_EXITING_MT_WHITNEY = "overnight-exiting-mt-whitney";
export const OVERNIGHT_PERMIT = "overnight-permit";
export const INYO_NF_PERMIT_TYPES = [
  OVERNIGHT_EXITING_MT_WHITNEY,
  OVERNIGHT_PERMIT
];

export const ADDITIONAL_ANIMAL_TEXT_FOR_MT_WHITNEY = 'NOTE: You cannot bring any stock to Mt. Whitney.';

// questionaire results
export const QUESTIONAIRE_RESULTS_ENUM = {
  MT_WHITNEY_DAY_USE_PERMIT: 'mt-whitney-day-use-permit',
  MT_WHITNEY_TRAIL_OVERNIGHT_PERMIT: 'mt-whitney-trail-overnight-permit',
  INYO_OVERNIGHT_EXITING_MT_WHITNEY: "overnight-exiting-mt-whitney",
  INYO_OVERNIGHT_PERMIT: "overnight-permit",
  INYO_OVERNIGHT_PERMIT_2: "overnight-permit-2",
  CONTACT_AGENCY: "contact-agency",
  CONTACT_COMMERCIAL_OPERATORS: "contact-commercial-operator",
  No_NEED_FOR_PERMIT: 'you-dont-need-permit'
};

export const INYO_GROUPSIZE_MAX_PERMIT_TYPE = {
  [OVERNIGHT_EXITING_MT_WHITNEY]: 15,
  [OVERNIGHT_PERMIT]: 15
};

export const SKU_INYO_VISITING_MT_WHITNEY = 'PERUSE233262VMW';
export const SKU_INYO = 'PERUSE233262';
export const SKU_MT_WHITNEY = `PERUSE${MT_WHITNEY_PERMIT_ID}`;
export const MAX_PETS_KEY_NAME = 'MaxPets';
export const STAY_LIMIT_RULE_NAME = 'StayLimit';
export const DOGS_OR_OTHER_PETS = 6;

export const COMMERCIAL_BOOKING_TEXT = 'Please contact the Commercial Wilderness Permit Office to book your reservation.';

export function getPermitTypeInyo(permitTypeStr) {
  if (permitTypeStr === OVERNIGHT_EXITING_MT_WHITNEY) {
    return 'OvernightExitingMtWhitney';
  }
  else if (permitTypeStr === OVERNIGHT_PERMIT) {
    return 'Overnight';
  }
  return '';
}

export function calculateEntrancesExits(locations, divisionId, divisions, isExitingMtWhitney) {
  // divisionId and divisions are optionally arguments
  let entrances = filter(locations, { is_entry: true });
  let exits = [];
  if (isExitingMtWhitney) {
    exits = filter(locations, (loc) => loc.is_exit && loc.id === INYO_MT_WHITNEY_EXIT_ID);
  }
  else {
    exits = filter(locations, (loc) => loc.is_exit && loc.id !== INYO_MT_WHITNEY_EXIT_ID);
  }
  const locationMap = keyBy(locations, 'id');

  if (divisionId && divisions) {
    const division = divisions[divisionId];
    if (division) {
      if (division.entry_ids) {
        entrances = division.entry_ids.map((entryId) => locationMap[entryId]);
      }
      if (division.exit_ids) {
        const exitIds = isExitingMtWhitney ? [INYO_MT_WHITNEY_EXIT_ID] :
          filter(division.exit_ids, (id) => id !== INYO_MT_WHITNEY_EXIT_ID);
        exits = exitIds.map((exitId) => locationMap[exitId]);
      }
    }
  }

  // sort entrances and exits alphabetically
  entrances = sortBy(entrances, 'name');
  exits = sortBy(exits, 'name');

  return {
    validEntrances: entrances,
    validExits: exits
  };
}

export function calculateFeesInyoLI(lineItems, groupSize) {
  if (lineItems && groupSize) {
    const feesMap = convertLineItemsToFeesMap(lineItems);
    const feesMapInyo = {};
    feesMapInyo[types.RESERVATION_FEE] = { count: 1, amount: feesMap.get(types.RESERVATION_FEE) };
    feesMapInyo[types.USE_FEE] = { count: groupSize, amount: feesMap.get(types.USE_FEE) };
    feesMapInyo[types.NONREFUNDABLE_FEE] = { count: 1, amount: feesMap.get(types.NONREFUNDABLE_FEE) };
    feesMapInyo.total = feesMapInyo[types.RESERVATION_FEE].amount + feesMapInyo[types.USE_FEE].amount + feesMapInyo[types.NONREFUNDABLE_FEE].amount;
    return feesMapInyo;
  }
  return {};
}

// Will leave this here for reference for a bit in case we need to do cleanup for other permit uis
// can safely be removed once fees are settled
export function calculateFeesInyo(fees, originalGroupMembers = [], permitType, isVisitingMtWhitney, guides = []) {
  if (typeof fees === 'undefined' || fees.length === 0) {
    return {};
  }

  // remove group members that are guides (don't count towards fee)
  let groupMembers = [];
  if (!isNil(originalGroupMembers)) {
    groupMembers = originalGroupMembers.slice(0);
  }
  if (!isNil(guides)) {
    groupMembers.length -= guides.length;
  }

  const feeMap = {};
  let total = 0;
  feeMap[types.RESERVATION_FEE] = { count: 0, amount: 0 };
  feeMap[types.USE_FEE] = { count: 0, amount: 0 };
  total = 0;
  fees.forEach((f) => {
    if (f.type === feeTypes.get(types.RESERVATION_FEE)) {
      if (!f.is_lottery) {
        feeMap[types.RESERVATION_FEE] = { count: 1, amount: parseInt(f.amount, 10) };
        total += parseInt(f.amount, 10);
      }
    }
    else if (f.type === feeTypes.get(types.USE_FEE)) {
      const correctFeeForVisitingMtWhitney = f.sku === SKU_INYO_VISITING_MT_WHITNEY &&
        (permitType === OVERNIGHT_EXITING_MT_WHITNEY || isVisitingMtWhitney);
      const correctFeeForNormalVisit = f.sku === SKU_INYO && !isVisitingMtWhitney &&
        permitType !== OVERNIGHT_EXITING_MT_WHITNEY;
      const mtWhitneyFee = f.sku === SKU_MT_WHITNEY;
      if (correctFeeForVisitingMtWhitney || correctFeeForNormalVisit || mtWhitneyFee) {
        const subtotal = parseInt(f.amount, 10) * groupMembers.length;
        feeMap[types.USE_FEE] = { count: groupMembers.length, amount: parseInt(f.amount, 10) };
        total += subtotal;
      }
    }
  });

  feeMap.total = total;
  return feeMap;
}

export const getClassName = (error, displayError) => {
  if (error && displayError) {
    return 'rec-error';
  }
  return '';
}

export const isQuotaModifyAllowed = (reservation, permit) => {
    const isAwardedLotteryApplication = reservation.status === issuanceStatuses.get(LOTTERY_WON)
    const isAcceptedAwardedReservationInProgress = reservation.lottery_awarded && reservation.status === issuanceStatuses.get(PERMIT_IN_PROGRESS)

    // can't modify quota of awarded lottery applications
    if (isAwardedLotteryApplication || isAcceptedAwardedReservationInProgress) {
        return false;
    }

    const isPreliminaryOrInProgressReservation = [PERMIT_PRELIMINARY, PERMIT_IN_PROGRESS]
        .map((st) => issuanceStatuses.get(st))
        .includes(reservation.status)
    if(isPreliminaryOrInProgressReservation) {
        return true
    }

    let changesAllowed = true;

    const modificationDeadline = readRuleValue(MODIFICATION_DEADLINE, permit.rules);
    if (modificationDeadline !== '') {
        const entryDate = moment.utc(reservation.start_date);
        const currentDate = moment().utc();
        const daysUntilReservation = entryDate.diff(currentDate, 'days');
        if (daysUntilReservation < modificationDeadline) {
            changesAllowed = false;
        }
    }
    return changesAllowed;
}

export const isFutureReservation = (reservation) => {
  const exitDate = moment.utc(reservation.end_date);

  return exitDate.isSameOrAfter(moment());
}

export const mustVisitMtWhitney = (reservation) => MTWHITNEY_VISIT_TRAILS.includes(reservation.division_id);

export const isExitingMtWhitney = (reservation) => reservation.permit_type === getPermitTypeInyo(OVERNIGHT_EXITING_MT_WHITNEY);

export const convertOvernightCampsites = (map) => {
  const obj = sortBy(keys(map)).map((key) => ({ date: key, name: map[key] }));
  return obj;
};

export const campsiteSelected = (campsitePreferences) => {
  return Object.keys(campsitePreferences).length ? 'true' : '';
}
